import { useState, useCallback } from 'react'
import { useMedia } from '@/hooks'
import { Button, Modal, LoadingOverlay } from '@/components/ui'
import { useToast } from '@/components/ui'
import { formatDate, formatFileSize } from '@/lib/utils'
import {
  Upload,
  Trash2,
  Copy,
  Image as ImageIcon,
  FileText,
  Grid,
  List,
  Check,
} from 'lucide-react'
import { ALLOWED_MEDIA_TYPES, MAX_FILE_SIZE } from '@/types/media'
import { cn } from '@/lib/utils'

interface MediaLibraryProps {
  onSelect?: (url: string) => void
  selectable?: boolean
}

export function MediaLibrary({ onSelect, selectable = false }: MediaLibraryProps) {
  const { showToast } = useToast()
  const { media, isLoading, hasMore, loadMore, uploadFile, deleteMedia } = useMedia()

  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [mediaToDelete, setMediaToDelete] = useState<string | null>(null)

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return

      // Validate
      if (!ALLOWED_MEDIA_TYPES.includes(file.type as typeof ALLOWED_MEDIA_TYPES[number])) {
        showToast('error', 'Type de fichier non supporte')
        return
      }
      if (file.size > MAX_FILE_SIZE) {
        showToast('error', 'Fichier trop volumineux (max 10 Mo)')
        return
      }

      setIsUploading(true)
      setUploadProgress(0)

      try {
        await uploadFile(file, setUploadProgress)
        showToast('success', 'Fichier uploade avec succes')
      } catch {
        showToast('error', 'Erreur lors de l\'upload')
      } finally {
        setIsUploading(false)
        setUploadProgress(0)
        // Reset input
        e.target.value = ''
      }
    },
    [uploadFile, showToast]
  )

  const handleDelete = useCallback(async () => {
    if (!mediaToDelete) return

    try {
      await deleteMedia(mediaToDelete)
      showToast('success', 'Fichier supprime')
      setShowDeleteModal(false)
      setMediaToDelete(null)
    } catch {
      showToast('error', 'Erreur lors de la suppression')
    }
  }, [deleteMedia, mediaToDelete, showToast])

  const handleCopy = useCallback(
    (url: string) => {
      navigator.clipboard.writeText(url)
      showToast('success', 'URL copiee dans le presse-papier')
    },
    [showToast]
  )

  const handleSelect = useCallback(
    (url: string, key: string) => {
      if (selectable && onSelect) {
        setSelectedKey(key)
        onSelect(url)
      }
    },
    [selectable, onSelect]
  )

  const confirmDelete = useCallback((key: string) => {
    setMediaToDelete(key)
    setShowDeleteModal(true)
  }, [])

  if (isLoading && media.length === 0) {
    return <LoadingOverlay message="Chargement des medias..." />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Medias</h1>

        <div className="flex items-center gap-3">
          {/* View mode toggle */}
          <div className="flex items-center border rounded-lg">
            <button
              onClick={() => setViewMode('grid')}
              className={cn(
                'p-2 rounded-l-lg',
                viewMode === 'grid' ? 'bg-gray-100' : 'hover:bg-gray-50'
              )}
              title="Vue grille"
            >
              <Grid className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={cn(
                'p-2 rounded-r-lg',
                viewMode === 'list' ? 'bg-gray-100' : 'hover:bg-gray-50'
              )}
              title="Vue liste"
            >
              <List className="h-4 w-4" />
            </button>
          </div>

          {/* Upload button */}
          <label className="cursor-pointer">
            <span className={`inline-flex items-center justify-center font-medium rounded-lg px-4 py-2 text-sm transition-colors bg-primary-600 text-white hover:bg-primary-700 ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
              <Upload className="h-4 w-4 mr-2" />
              {isUploading ? `Upload... ${uploadProgress}%` : 'Uploader'}
            </span>
            <input
              type="file"
              accept={ALLOWED_MEDIA_TYPES.join(',')}
              onChange={handleFileSelect}
              className="hidden"
              disabled={isUploading}
            />
          </label>
        </div>
      </div>

      {/* Media grid/list */}
      {media.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border">
          <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-4 text-gray-500">Aucun media uploade</p>
          <p className="text-sm text-gray-400">
            Cliquez sur "Uploader" pour ajouter des fichiers
          </p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {media.map((item) => (
            <MediaCard
              key={item.key}
              item={item}
              selected={selectedKey === item.key}
              selectable={selectable}
              onSelect={() => handleSelect(item.url, item.key)}
              onCopy={() => handleCopy(item.url)}
              onDelete={() => confirmDelete(item.key)}
            />
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg border divide-y">
          {media.map((item) => (
            <MediaRow
              key={item.key}
              item={item}
              selected={selectedKey === item.key}
              selectable={selectable}
              onSelect={() => handleSelect(item.url, item.key)}
              onCopy={() => handleCopy(item.url)}
              onDelete={() => confirmDelete(item.key)}
            />
          ))}
        </div>
      )}

      {/* Load more */}
      {hasMore && (
        <div className="text-center">
          <Button variant="secondary" onClick={loadMore}>
            Charger plus
          </Button>
        </div>
      )}

      {/* Delete confirmation modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Confirmer la suppression"
        size="sm"
      >
        <p className="text-gray-600">
          Etes-vous sur de vouloir supprimer ce fichier ? Cette action est irreversible.
        </p>
        <div className="mt-6 flex justify-end gap-3">
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Annuler
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Supprimer
          </Button>
        </div>
      </Modal>
    </div>
  )
}

interface MediaItemProps {
  item: {
    key: string
    url: string
    size: number
    contentType: string
    uploadedAt: string
  }
  selected: boolean
  selectable: boolean
  onSelect: () => void
  onCopy: () => void
  onDelete: () => void
}

function MediaCard({
  item,
  selected,
  selectable,
  onSelect,
  onCopy,
  onDelete,
}: MediaItemProps) {
  const isImage = item.contentType.startsWith('image/')

  return (
    <div
      className={cn(
        'relative group bg-white rounded-lg border overflow-hidden',
        selectable && 'cursor-pointer',
        selected && 'ring-2 ring-primary-500'
      )}
      onClick={selectable ? onSelect : undefined}
    >
      {/* Thumbnail */}
      <div className="aspect-square bg-gray-100 flex items-center justify-center">
        {isImage ? (
          <img
            src={item.url}
            alt={item.key}
            className="w-full h-full object-cover"
          />
        ) : (
          <FileText className="h-12 w-12 text-gray-400" />
        )}
      </div>

      {/* Selected indicator */}
      {selected && (
        <div className="absolute top-2 right-2 p-1 bg-primary-500 rounded-full">
          <Check className="h-4 w-4 text-white" />
        </div>
      )}

      {/* Actions overlay */}
      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
        <button
          onClick={(e) => {
            e.stopPropagation()
            onCopy()
          }}
          className="p-2 bg-white rounded-full hover:bg-gray-100"
          title="Copier l'URL"
        >
          <Copy className="h-4 w-4" />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation()
            onDelete()
          }}
          className="p-2 bg-white rounded-full hover:bg-red-50 text-red-600"
          title="Supprimer"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      {/* Info */}
      <div className="p-2">
        <p className="text-xs text-gray-600 truncate">{item.key}</p>
        <p className="text-xs text-gray-400">{formatFileSize(item.size)}</p>
      </div>
    </div>
  )
}

function MediaRow({
  item,
  selected,
  selectable,
  onSelect,
  onCopy,
  onDelete,
}: MediaItemProps) {
  const isImage = item.contentType.startsWith('image/')

  return (
    <div
      className={cn(
        'flex items-center gap-4 p-4 hover:bg-gray-50',
        selectable && 'cursor-pointer',
        selected && 'bg-primary-50'
      )}
      onClick={selectable ? onSelect : undefined}
    >
      {/* Thumbnail */}
      <div className="w-16 h-16 bg-gray-100 rounded flex items-center justify-center flex-shrink-0">
        {isImage ? (
          <img
            src={item.url}
            alt={item.key}
            className="w-full h-full object-cover rounded"
          />
        ) : (
          <FileText className="h-8 w-8 text-gray-400" />
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{item.key}</p>
        <p className="text-sm text-gray-500">
          {formatFileSize(item.size)} • {formatDate(item.uploadedAt)}
        </p>
      </div>

      {/* Selected indicator */}
      {selected && (
        <Check className="h-5 w-5 text-primary-500" />
      )}

      {/* Actions */}
      <div className="flex items-center gap-2">
        <button
          onClick={(e) => {
            e.stopPropagation()
            onCopy()
          }}
          className="p-2 hover:bg-gray-100 rounded"
          title="Copier l'URL"
        >
          <Copy className="h-4 w-4 text-gray-600" />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation()
            onDelete()
          }}
          className="p-2 hover:bg-red-50 rounded text-red-600"
          title="Supprimer"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}
